import { PtiItemType } from "../../api/ptiApi";

/** Canonical PTI position keys (aligned with backend PtiPositionKeys). */
export const PTI_POSITION_KEYS = {
    Front: "front",
    Rear: "rear",
    DriverSide: "driver_side",
    PassengerSide: "passenger_side",
    Dashboard: "dashboard",
    Coolant: "coolant",
    Oil: "oil",
    FifthWheel: "fifth_wheel",
    Doors: "doors",
    LeftInside: "left_inside",
    RightInside: "right_inside",
    InsideFloor: "inside_floor",
    SteerLeft: "steer_left",
    SteerRight: "steer_right",
    Drive1LeftOuter: "drive_1_left_outer",
    Drive1LeftInner: "drive_1_left_inner",
    Drive1RightInner: "drive_1_right_inner",
    Drive1RightOuter: "drive_1_right_outer",
    Drive2LeftOuter: "drive_2_left_outer",
    Drive2LeftInner: "drive_2_left_inner",
    Drive2RightInner: "drive_2_right_inner",
    Drive2RightOuter: "drive_2_right_outer",
    Axle1LeftOuter: "axle_1_left_outer",
    Axle1LeftInner: "axle_1_left_inner",
    Axle1RightInner: "axle_1_right_inner",
    Axle1RightOuter: "axle_1_right_outer",
    Axle2LeftOuter: "axle_2_left_outer",
    Axle2LeftInner: "axle_2_left_inner",
    Axle2RightInner: "axle_2_right_inner",
    Axle2RightOuter: "axle_2_right_outer",
} as const;

export type PtiPositionKey = (typeof PTI_POSITION_KEYS)[keyof typeof PTI_POSITION_KEYS];

/** Checklist section titles (aligned with backend PtiChecklistSections). */
export const PTI_CHECKLIST_SECTIONS = {
    ExteriorCheck: "Exterior Check",
    BrakeSystemCheck: "Brake System Check",
    CabInteriorCheck: "Cab Interior Check",
    DocumentsCheck: "Documents Check",
    AirBrakeTest: "Air Brake Test",
    ExteriorAndStructure: "Exterior & Structure",
    AirAndTires: "Air & Tires",
    Interior: "Interior",
} as const;

/** YesNoNa sentinel stored in checklist JSON. */
export const PTI_CHECKLIST_ANSWER_NA = "na";

export const PTI_STATUS = {
    Submitted: "Submitted",
    Completed: "Completed",
    Rejected: "Rejected",
} as const;

export const PTI_POSITION_LABELS: Record<string, string> = {
    [PTI_POSITION_KEYS.Front]: "Front",
    [PTI_POSITION_KEYS.Rear]: "Rear",
    [PTI_POSITION_KEYS.DriverSide]: "Driver Side",
    [PTI_POSITION_KEYS.PassengerSide]: "Passenger Side",
    [PTI_POSITION_KEYS.Dashboard]: "Dashboard",
    [PTI_POSITION_KEYS.Coolant]: "Coolant",
    [PTI_POSITION_KEYS.Oil]: "Oil",
    [PTI_POSITION_KEYS.FifthWheel]: "Fifth Wheel",
    [PTI_POSITION_KEYS.Doors]: "Doors",
    [PTI_POSITION_KEYS.LeftInside]: "Left Inside",
    [PTI_POSITION_KEYS.RightInside]: "Right Inside",
    [PTI_POSITION_KEYS.InsideFloor]: "Inside Floor",
    [PTI_POSITION_KEYS.SteerLeft]: "Steer Left",
    [PTI_POSITION_KEYS.SteerRight]: "Steer Right",
    [PTI_POSITION_KEYS.Drive1LeftOuter]: "Drive Axle 1 Left Outer",
    [PTI_POSITION_KEYS.Drive1LeftInner]: "Drive Axle 1 Left Inner",
    [PTI_POSITION_KEYS.Drive1RightInner]: "Drive Axle 1 Right Inner",
    [PTI_POSITION_KEYS.Drive1RightOuter]: "Drive Axle 1 Right Outer",
    [PTI_POSITION_KEYS.Drive2LeftOuter]: "Drive Axle 2 Left Outer",
    [PTI_POSITION_KEYS.Drive2LeftInner]: "Drive Axle 2 Left Inner",
    [PTI_POSITION_KEYS.Drive2RightInner]: "Drive Axle 2 Right Inner",
    [PTI_POSITION_KEYS.Drive2RightOuter]: "Drive Axle 2 Right Outer",
    drive_1_left: "Drive Axle 1 Left",
    drive_1_right: "Drive Axle 1 Right",
    drive_2_left: "Drive Axle 2 Left",
    drive_2_right: "Drive Axle 2 Right",
    [PTI_POSITION_KEYS.Axle1LeftOuter]: "Axle 1 Left Outer",
    [PTI_POSITION_KEYS.Axle1LeftInner]: "Axle 1 Left Inner",
    [PTI_POSITION_KEYS.Axle1RightInner]: "Axle 1 Right Inner",
    [PTI_POSITION_KEYS.Axle1RightOuter]: "Axle 1 Right Outer",
    [PTI_POSITION_KEYS.Axle2LeftOuter]: "Axle 2 Left Outer",
    [PTI_POSITION_KEYS.Axle2LeftInner]: "Axle 2 Left Inner",
    [PTI_POSITION_KEYS.Axle2RightInner]: "Axle 2 Right Inner",
    [PTI_POSITION_KEYS.Axle2RightOuter]: "Axle 2 Right Outer",
    axle_1_left: "Axle 1 Left",
    axle_1_right: "Axle 1 Right",
    axle_2_left: "Axle 2 Left",
    axle_2_right: "Axle 2 Right",
};

export const getPtiPositionLabel = (positionKey: string) =>
    PTI_POSITION_LABELS[positionKey] ?? positionKey.split("_").join(" ");

export const getPtiItemTypeLabel = (itemType: PtiItemType) =>
    itemType === "Tire" ? "Tire" : "Body photo";

export const PTI_STATUS_LABELS: Record<string, string> = {
    [PTI_STATUS.Submitted]: "Pending Review",
    [PTI_STATUS.Completed]: "Approved",
    [PTI_STATUS.Rejected]: "Rejected",
};

export const getPtiStatusLabel = (status: string) =>
    PTI_STATUS_LABELS[status] ?? status;

export const PTI_COMPLIANCE_STATUS_LABELS: Record<string, string> = {
    Overdue: "Overdue",
    DueSoon: "Due soon",
    Compliant: "Compliant",
};

export const getPtiComplianceStatusLabel = (status: string) =>
    PTI_COMPLIANCE_STATUS_LABELS[status] ?? status;

export const PTI_COMPLIANCE_STATUS_FILTER_OPTIONS = [
    { value: "needsAttention", label: "Needs attention" },
    { value: "overdue", label: "Overdue" },
    { value: "dueSoon", label: "Due soon" },
    { value: "compliant", label: "Compliant" },
    { value: "all", label: "All" },
];

export const PTI_STATUS_FILTER_OPTIONS = [
    { value: PTI_STATUS.Submitted, label: "Pending Review" },
    { value: PTI_STATUS.Completed, label: "Approved" },
    { value: PTI_STATUS.Rejected, label: "Rejected" },
];

export function getPtiAssetDisplayLabel(item: {
    assetType: string;
    assetDisplayLabel?: string | null;
    vehicleName?: string | null;
    vehicleUnitNumber?: string | null;
    trailerUnitNumber?: string | null;
    vehicleId?: number | null;
    trailerId?: number | null;
}): string {
    if (item.assetDisplayLabel) {
        return item.assetDisplayLabel;
    }

    if (item.assetType === "Vehicle") {
        if (item.vehicleUnitNumber && item.vehicleName) {
            return `${item.vehicleUnitNumber} · ${item.vehicleName}`;
        }
        return item.vehicleUnitNumber || item.vehicleName || (item.vehicleId ? `Vehicle #${item.vehicleId}` : "Vehicle");
    }

    return item.trailerUnitNumber || (item.trailerId ? `Trailer #${item.trailerId}` : "Trailer");
}
